import type { AppState } from '../../state/appState.js';
import { cloneEdges } from '../documentClone.js';
import { cloneGraphNode, getNodeDataRecord } from '../../graph/nodeDefs.js';
import type { GraphEdge, GraphNode } from '../../graph/nodeDefs.js';
import type {
  ConnectCmd,
  CreateNodeCmd,
  CutEdgesCmd,
  DeleteNodeCmd,
  MoveNodeCmd,
  ReconnectEdgeCmd,
  SetNodeDataCmd,
  ReplaceNodeCmd,
  SetParamValueCmd,
} from '../commandBuilders.js';
import type { ActionBase, ActionExecution, EditorAction } from '../commandExecutor.js';
import {
  deepEqualValue,
  addEdgeFor,
  edgeExistsFor,
  findIncomingEdgeFor,
  removeEdgeFor,
  sameEdge,
} from './shared.js';

export interface DropNodeAction extends ActionBase<'graph:dropNode'> {
  nodeId: string;
}

export interface RestoreNodeAction extends ActionBase<'graph:restoreNode'> {
  node: GraphNode;
  edges: GraphEdge[];
}

export interface AddEdgeAction extends ActionBase<'graph:addEdge'> {
  edge: ConnectCmd['edge'];
}

export interface RemoveEdgeAction extends ActionBase<'graph:removeEdge'> {
  edge: ConnectCmd['edge'];
}

export interface DeleteParamValueAction extends ActionBase<'params:deleteValue'> {
  key: string;
}

export function execCreateNodeFor(appState: AppState, cmd: CreateNodeCmd): ActionExecution<import('../../graph/nodeDefs.js').GraphNode | null> {
  if (appState.nodes.some(node => node.id === cmd.node.id)) {
    return { result: appState.nodes.find(node => node.id === cmd.node.id) ?? null, undoActions: [], changed: false };
  }

  appState.nodes = [...appState.nodes, cloneGraphNode(cmd.node)];
  appState.editorGraphDirty = true;
  appState.selectedNode = cmd.node.id;

  return {
    result: cmd.node,
    undoActions: [{ type: 'graph:dropNode', nodeId: cmd.node.id }],
    changed: true,
  };
}

export function execDropNodeFor(appState: AppState, cmd: DropNodeAction): ActionExecution<void> {
  const removed = appState.nodes.some(node => node.id === cmd.nodeId);
  appState.nodes = appState.nodes.filter(node => node.id !== cmd.nodeId);
  appState.edges = appState.edges.filter(edge => edge.fromNode !== cmd.nodeId && edge.toNode !== cmd.nodeId);
  if (appState.selectedNode === cmd.nodeId) appState.selectedNode = null;
  if (removed) appState.editorGraphDirty = true;
  return { result: undefined, undoActions: [], changed: removed };
}

export function execDeleteNodeFor(appState: AppState, cmd: DeleteNodeCmd): ActionExecution<void> {
  const node = appState.nodes.find(candidate => candidate.id === cmd.nodeId);
  if (!node) return { result: undefined, undoActions: [], changed: false };
  const removedEdges = appState.edges.filter(edge => edge.fromNode === cmd.nodeId || edge.toNode === cmd.nodeId);

  appState.nodes = appState.nodes.filter(candidate => candidate.id !== cmd.nodeId);
  appState.edges = appState.edges.filter(edge => edge.fromNode !== cmd.nodeId && edge.toNode !== cmd.nodeId);
  if (appState.selectedNode === cmd.nodeId) appState.selectedNode = null;
  appState.editorGraphDirty = true;

  return {
    result: undefined,
    undoActions: [{
      type: 'graph:restoreNode',
      node: cloneGraphNode(node),
      edges: cloneEdges(removedEdges),
    }],
    changed: true,
  };
}

export function execRestoreNodeFor(appState: AppState, cmd: RestoreNodeAction): ActionExecution<void> {
  if (!appState.nodes.some(node => node.id === cmd.node.id)) {
    appState.nodes = [...appState.nodes, cloneGraphNode(cmd.node)];
  }

  for (const edge of cmd.edges) {
    addEdgeFor(appState, edge);
  }

  appState.editorGraphDirty = true;

  return { result: undefined, undoActions: [], changed: true };
}

export function execMoveNodeFor(appState: AppState, cmd: MoveNodeCmd): ActionExecution<void> {
  const node = appState.nodes.find(candidate => candidate.id === cmd.nodeId);
  if (!node) return { result: undefined, undoActions: [], changed: false };
  if (node.x === cmd.x && node.y === cmd.y) return { result: undefined, undoActions: [], changed: false };

  const undo: MoveNodeCmd = { type: 'graph:moveNode', nodeId: cmd.nodeId, x: node.x, y: node.y };
  node.x = cmd.x;
  node.y = cmd.y;
  appState.editorGraphDirty = true;

  return { result: undefined, undoActions: [undo], changed: true };
}

export function execSetDataFor(appState: AppState, cmd: SetNodeDataCmd): ActionExecution<void> {
  const node = appState.nodes.find(candidate => candidate.id === cmd.nodeId);
  if (!node) return { result: undefined, undoActions: [], changed: false };
  const nodeData = getNodeDataRecord(node);
  const previous = nodeData[cmd.field];
  if (Object.is(previous, cmd.value)) return { result: undefined, undoActions: [], changed: false };

  nodeData[cmd.field] = cmd.value;
  appState.editorGraphDirty = true;

  return {
    result: undefined,
    undoActions: [{ type: 'graph:setData', nodeId: cmd.nodeId, field: cmd.field, value: previous }],
    changed: true,
  };
}

export function execReplaceNodeFor(appState: AppState, cmd: ReplaceNodeCmd): ActionExecution<void> {
  const index = appState.nodes.findIndex(candidate => candidate.id === cmd.nodeId);
  if (index < 0 || cmd.node.id !== cmd.nodeId) return { result: undefined, undoActions: [], changed: false };
  const previous = cloneGraphNode(appState.nodes[index]);
  const previousIncoming = appState.edges.filter(edge => edge.toNode === cmd.nodeId);
  const outgoing = appState.edges.filter(edge => edge.fromNode === cmd.nodeId);
  appState.nodes = appState.nodes.map((node, nodeIndex) => nodeIndex === index ? cloneGraphNode(cmd.node) : node);
  appState.edges = [
    ...appState.edges.filter(edge => edge.fromNode !== cmd.nodeId && edge.toNode !== cmd.nodeId),
    ...outgoing,
    ...cloneEdges(cmd.incomingEdges),
  ];
  appState.editorGraphDirty = true;
  return {
    result: undefined,
    changed: true,
    undoActions: [{ type: 'graph:replaceNode', nodeId: cmd.nodeId, node: previous, incomingEdges: cloneEdges(previousIncoming) }],
  };
}

export function execAddEdgeFor(appState: AppState, cmd: AddEdgeAction): ActionExecution<void> {
  const changed = addEdgeFor(appState, cmd.edge);
  if (changed) appState.editorGraphDirty = true;
  return { result: undefined, undoActions: [], changed };
}

export function execRemoveEdgeFor(appState: AppState, cmd: RemoveEdgeAction): ActionExecution<void> {
  const changed = removeEdgeFor(appState, cmd.edge);
  if (changed) appState.editorGraphDirty = true;
  return { result: undefined, undoActions: [], changed };
}

export function execConnectFor(appState: AppState, cmd: ConnectCmd): ActionExecution<void> {
  const previousIncoming = findIncomingEdgeFor(appState, cmd.edge.toNode, cmd.edge.toPort);
  const hadExact = previousIncoming && sameEdge(previousIncoming, cmd.edge);
  if (hadExact) return { result: undefined, undoActions: [], changed: false };

  const undoActions: EditorAction[] = [{ type: 'graph:removeEdge', edge: { ...cmd.edge } }];

  if (previousIncoming && !sameEdge(previousIncoming, cmd.edge)) {
    removeEdgeFor(appState, previousIncoming);
    undoActions.push({ type: 'graph:addEdge', edge: { ...previousIncoming } });
  }

  addEdgeFor(appState, cmd.edge);
  appState.editorGraphDirty = true;
  return { result: undefined, undoActions, changed: true };
}

export function execReconnectFor(appState: AppState, cmd: ReconnectEdgeCmd): ActionExecution<void> {
  if (sameEdge(cmd.fromEdge, cmd.toEdge)) {
    return { result: undefined, undoActions: [], changed: false };
  }

  const replacedIncoming = findIncomingEdgeFor(appState, cmd.toEdge.toNode, cmd.toEdge.toPort);
  const undoActions: EditorAction[] = [];

  removeEdgeFor(appState, cmd.fromEdge);
  if (replacedIncoming && !sameEdge(replacedIncoming, cmd.fromEdge) && !sameEdge(replacedIncoming, cmd.toEdge)) {
    removeEdgeFor(appState, replacedIncoming);
    undoActions.push({ type: 'graph:addEdge', edge: { ...replacedIncoming } });
  }

  addEdgeFor(appState, cmd.toEdge);
  undoActions.unshift({ type: 'graph:removeEdge', edge: { ...cmd.toEdge } });
  undoActions.push({ type: 'graph:addEdge', edge: { ...cmd.fromEdge } });
  appState.editorGraphDirty = true;
  return { result: undefined, undoActions, changed: true };
}

export function execCutEdgesFor(appState: AppState, cmd: CutEdgesCmd): ActionExecution<void> {
  const removed = cmd.edges.filter(edge => edgeExistsFor(appState, edge));
  if (removed.length === 0) return { result: undefined, undoActions: [], changed: false };
  appState.edges = appState.edges.filter(edge => !removed.some(candidate => sameEdge(candidate, edge)));
  appState.editorGraphDirty = true;
  return {
    result: undefined,
    undoActions: removed.map(edge => ({ type: 'graph:addEdge', edge: { ...edge } })),
    changed: true,
  };
}

export function execSetParamValueFor(appState: AppState, cmd: SetParamValueCmd): ActionExecution<void> {
  const previous = appState.params[cmd.key];
  if (deepEqualValue(previous, cmd.value)) return { result: undefined, undoActions: [], changed: false };

  appState.params[cmd.key] = cmd.value as typeof appState.params[string];
  const undoActions: EditorAction[] = previous === undefined
    ? [{ type: 'params:deleteValue', key: cmd.key }]
    : [{ type: 'params:setValue', key: cmd.key, value: previous }];
  return { result: undefined, undoActions, changed: true };
}

export function execDeleteParamValueFor(appState: AppState, cmd: DeleteParamValueAction): ActionExecution<void> {
  if (!(cmd.key in appState.params)) return { result: undefined, undoActions: [], changed: false };
  const previous = appState.params[cmd.key];
  delete appState.params[cmd.key];
  return {
    result: undefined,
    undoActions: [{ type: 'params:setValue', key: cmd.key, value: previous }],
    changed: true,
  };
}
